import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { IOrganisation, IOrganisationsHierarchy, OrganisationDataService } from '@services/organisation-data.service';
import { Account, AccountsService, IAccount, IAccountUser } from '@services/accounts.service';
import { ILevelAsset } from '@services/assets-data.service';
import { ApplicationSettings } from './application.service';

/**
 * siteSubscriptions
 */
@Injectable({
	providedIn: 'root'
})
export class siteSubscriptions {

	/** hierarchical Data */
	public applicationSettings$: Observable<ApplicationSettings>;
	/** hierarchical Data Subject*/
	private applicationSettingsSubject: BehaviorSubject<ApplicationSettings>;

	/** hierarchical Data */
	public hierarchicalData$: Observable<string>;
	/** hierarchical Data Subject*/
	private hierarchicalDataSubject: BehaviorSubject<string>;

	/** hierarchical Location Data */
	public selectedHierarchicalLocation$: Observable<IOrganisationsHierarchy>;
	/** hierarchical Location Data Subject*/
	private selectedHierarchicalLocationSubject: BehaviorSubject<IOrganisationsHierarchy>;

	/** Selected Asset */
	public selectedAsset$: Observable<any>;
	/** Selected Asset Subject*/
	private selectedAssetSubject: BehaviorSubject<any>;

	/** My Organisations Subject */
	private myOrganisationsSubject: BehaviorSubject<IOrganisation[]>;
	/** List of My Organisations in JSON Format*/
	public myOrganisations$: Observable<IOrganisation[]>;

	/** Selected Organisation*/
	public selectedOrganisation$: Observable<IOrganisation>;
	/** Selected Organisation Subject*/
	private selectedOrganisationSubject: BehaviorSubject<IOrganisation>;

	/** Selected Organisation ID*/
	public selectedOrganisationUID$: Observable<string>;
	/** Selected Organisation ID Subject*/
	private selectedOrganisationUIDSubject: BehaviorSubject<string>;

	/** Selected Sub Location */
	public selectedSubLocation$: Observable<string>;
	/** Selected Sub Location Subject*/
	private selectedSubLocationSubject: BehaviorSubject<string>;

	/** My Accounts Subject */
	private myAccountsSubject: BehaviorSubject<IAccount[]>;
	/** List of My Accounts in JSON Format*/
	public myAccounts$: Observable<IAccount[]>;

	/** Selected Account Subject */
	private selectedAccountSubject: BehaviorSubject<IAccount>;
	/** Selected Account */
	public selectedAccount$: Observable<IAccount>;

	/** Selected Account GID Subject*/
	private selectedAccountGUIDSubject: BehaviorSubject<string>;
	/** Selected Account GIID*/
	public selectedAccountGUID$: Observable<string>;

	/** Account Details */
	private accountDetailsSubject: BehaviorSubject<any>;
	/** Account Details */
	public accountDetails$: Observable<any>;

	/** Account Systems */
	private accountSystemsSubject: BehaviorSubject<any>;
	/** Account Systems */
	public accountSystems$: Observable<any>;

	/** Account Users */
	private accountUsersSubject: BehaviorSubject<any>;
	/** Account Users */
	public accountUsers$: Observable<any>;

	/** Current System */
	private currentSystemSubject: BehaviorSubject<any>;
	/** Current System */
	public currentSystem$: Observable<any>;

	/** System Name */
	private currentSystemNameSubject: BehaviorSubject<any>;
	/** System Name */
	public currentSystemName$: Observable<any>;

	/** Refresh User List */
	private refreshUserListSubject: BehaviorSubject<any>;
	/** Refresh User List */
	public refreshUserList$: Observable<any>;

	/** Refresh Watch List */
	private refreshWatchListSubject: BehaviorSubject<any>;
	/** Refresh Watch List */
	public refreshWatchList$: Observable<any>;

	/** Refresh Chart */
	private refreshChartSubject: BehaviorSubject<any>;
	/** Refresh Chart */
	public refreshChart$: Observable<any>;

	/** Refresh Chart */
	private clearAndRefreshChartSubject: BehaviorSubject<any>;
	/** Refresh Chart */
	public clearAndRefreshChart$: Observable<any>;

	/** Update Model List */
	private updateModelListSubject: BehaviorSubject<any>;
	/** Update Model List */
	public updateModelList$: Observable<any>;

	/** Update Model Edits */
	private refreshModelEditsSubject: BehaviorSubject<any>;
	/** Update Model Edits */
	public refreshModelEdits$: Observable<any>;

	/** Refresh Tile Values */
	private refreshTileValuesSubject: BehaviorSubject<any>;
	/** Refresh Tile Values */
	public refreshTileValues$: Observable<any>;

	/** Redraw Dashboard */
	private redrawDashboardSubject: BehaviorSubject<any>;
	/** Redraw Dashboard */
	public redrawDashboard$: Observable<any>;

	/** Account Service */
	private accountsService: AccountsService;
	/** Organisation Data Service */
	private organisationDataService: OrganisationDataService;

	/**
	 * Default Constructor
	 * @param globalFunctions Global Functions
	 * @param injector injector
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		// private accountsService: AccountsService,
		// private organisationDataService: OrganisationDataService
		private injector: Injector  // <-- lazy injector
	) {

		try {

			this.applicationSettingsSubject = new BehaviorSubject<ApplicationSettings>(<ApplicationSettings>{});
			this.applicationSettings$ = this.applicationSettingsSubject.asObservable();

			this.myAccountsSubject = new BehaviorSubject<IAccount[]>(JSON.parse(localStorage.getItem("myAccounts") ?? "[]"));
			this.myAccounts$ = this.myAccountsSubject.asObservable();

			// this.selectedAccountSubject = new BehaviorSubject<IAccount>(JSON.parse(localStorage.getItem("selectedAccount") ?? "{}"));
			this.selectedAccountSubject = new BehaviorSubject<IAccount>(JSON.parse("{}"));
			this.selectedAccount$ = this.selectedAccountSubject.asObservable();

			this.selectedAccountGUIDSubject = new BehaviorSubject<string>(localStorage.getItem("selectedAccountGUID") || "");
			this.selectedAccountGUID$ = this.selectedAccountGUIDSubject.asObservable();

			this.myOrganisationsSubject = new BehaviorSubject<IOrganisation[]>(JSON.parse(localStorage.getItem("myOrganisations") ?? "[]"));
			this.myOrganisations$ = this.myOrganisationsSubject.asObservable();

			// this.selectedOrganisationSubject = new BehaviorSubject<IOrganisation>(JSON.parse(localStorage.getItem("selectedOrganisation") ?? "{}"));
			this.selectedOrganisationSubject = new BehaviorSubject<IOrganisation>(JSON.parse("{}"));
			this.selectedOrganisation$ = this.selectedOrganisationSubject.asObservable();

			this.selectedOrganisationUIDSubject = new BehaviorSubject<string>(localStorage.getItem("selectedOrganisationUID") || "");
			this.selectedOrganisationUID$ = this.selectedOrganisationUIDSubject.asObservable();

			this.selectedSubLocationSubject = new BehaviorSubject<string>(localStorage.getItem("selectedSubLocation") || "");
			this.selectedSubLocation$ = this.selectedSubLocationSubject.asObservable();

			this.hierarchicalDataSubject = new BehaviorSubject<string>("");
			this.hierarchicalData$ = this.hierarchicalDataSubject.asObservable();

			this.selectedHierarchicalLocationSubject = new BehaviorSubject<IOrganisationsHierarchy>(JSON.parse(localStorage.getItem("hierarchicalLocationData") || "{}"));
			this.selectedHierarchicalLocation$ = this.selectedHierarchicalLocationSubject.asObservable();

			this.selectedAssetSubject = new BehaviorSubject<string>(localStorage.getItem("selectedAsset") || "");
			this.selectedAsset$ = this.selectedAssetSubject.asObservable();

			this.accountDetailsSubject = new BehaviorSubject<string>(localStorage.getItem("accountDetails") || "");
			this.accountDetails$ = this.accountDetailsSubject.asObservable();

			this.accountSystemsSubject = new BehaviorSubject<string>(localStorage.getItem("accountSystems") || "");
			this.accountSystems$ = this.accountSystemsSubject.asObservable();

			this.accountUsersSubject = new BehaviorSubject<any>(localStorage.getItem("accountUsers"));
			this.accountUsers$ = this.accountUsersSubject.asObservable();

			this.currentSystemSubject = new BehaviorSubject<any>(localStorage.getItem("currentSystem"));
			this.currentSystem$ = this.currentSystemSubject.asObservable();

			this.currentSystemNameSubject = new BehaviorSubject<any>(localStorage.getItem("currentSystemName"));
			this.currentSystemName$ = this.currentSystemNameSubject.asObservable();

			this.refreshUserListSubject = new BehaviorSubject<any>("");
			this.refreshUserList$ = this.refreshUserListSubject.asObservable();

			this.refreshWatchListSubject = new BehaviorSubject<any>("");
			this.refreshWatchList$ = this.refreshWatchListSubject.asObservable();

			this.refreshChartSubject = new BehaviorSubject<any>("");
			this.refreshChart$ = this.refreshChartSubject.asObservable();

			this.clearAndRefreshChartSubject = new BehaviorSubject<any>("");
			this.clearAndRefreshChart$ = this.clearAndRefreshChartSubject.asObservable();

			this.updateModelListSubject = new BehaviorSubject<any>("");
			this.updateModelList$ = this.updateModelListSubject.asObservable();

			this.refreshModelEditsSubject = new BehaviorSubject<any>("");
			this.refreshModelEdits$ = this.refreshModelEditsSubject.asObservable();

			this.refreshTileValuesSubject = new BehaviorSubject<any>("");
			this.refreshTileValues$ = this.refreshTileValuesSubject.asObservable();

			this.redrawDashboardSubject = new BehaviorSubject<any>("");
			this.redrawDashboard$ = this.redrawDashboardSubject.asObservable();
		}
		catch (error) {

		}
	}

	/**
	 * getAccountsService
	 * @returns 
	 */
	private getAccountsService(): AccountsService {
		if (!this.accountsService) {
			this.accountsService = this.injector.get(AccountsService);
		}
		return this.accountsService;
	}

	/**
	 * getOrganisationDataService
	 * @returns 
	 */
	private getOrganisationDataService(): OrganisationDataService {
		if (!this.organisationDataService) {
			this.organisationDataService = this.injector.get(OrganisationDataService);
		}
		return this.organisationDataService;
	}

	/**
	 * Clear Local Storage Values
	 */
	ClearLocalStorage() {

		localStorage.removeItem("appName");
		localStorage.removeItem("myAccounts");
		localStorage.removeItem("selectedAccount");
		// localStorage.removeItem("selectedAccountGUID");
		localStorage.removeItem("myOrganisations");
		localStorage.removeItem("selectedOrganisation");
		// localStorage.removeItem("selectedOrganisationUID");
		localStorage.removeItem("selectedSubLocation");
		localStorage.removeItem("hierarchicalData");
		localStorage.removeItem("selectedAsset");
		localStorage.removeItem("accountDetails");
		localStorage.removeItem("accountSystems");
		localStorage.removeItem("accountUsers");
		localStorage.removeItem("currentSystem");
		localStorage.removeItem("currentSystemName");
		localStorage.removeItem("HeirarchyExpandedNodes");
		localStorage.removeItem("defaultAccount");
		localStorage.removeItem("currentUser");
	}


	/**
	 * setApplicationConfiguration
	 * @param config 
	 */
	public setApplicationSettings(config:ApplicationSettings, forceTrigger: boolean = false){

		const currentValue = this.applicationSettingsSubject.getValue();
		if ((currentValue && JSON.stringify(currentValue) ==  JSON.stringify(config)) && !forceTrigger) {
			return; // ❌ Prevent unnecessary emission
		}

		this.applicationSettingsSubject.next(config);
	}

	/**
	 * Set the Structure
	 * @param value
	 */
	SethierarchicalData(value: IOrganisationsHierarchy[]) {
		this.hierarchicalDataSubject.next(JSON.stringify(value));
		this.SetSelectedSubLocation(value[0].Uid);
		if (value) { localStorage.setItem("hierarchicalData", JSON.stringify(value)); }
		else { localStorage.removeItem("hierarchicalData"); }
	}

	/**
	 * Set the Selected Asset
	 * @param value
	 */
	SetSelectedAsset(value: ILevelAsset, forceTrigger: boolean = false) {

		if (!value || Object.keys(value).length === 0) {
			this.selectedAssetSubject.next(null);
			localStorage.removeItem("selectedAsset");
			return;
		}

		const currentValue = this.selectedAssetSubject.getValue();
		if ((currentValue && currentValue.AssetID === value.AssetID) && !forceTrigger) {
			return; // ❌ Prevent unnecessary emission
		}

		this.selectedAssetSubject.next(value);
		if (value) { localStorage.setItem("selectedAsset", JSON.stringify(value)); }
		else { localStorage.removeItem("selectedAsset"); }
	}

	/**
	 * Set the Selected Account
	 * @param value
	 * @param forceTrigger
	 */
	SetSelectedAccount(value: IAccount = {} as IAccount, forceTrigger: boolean = false) {
		localStorage.removeItem("selectedAccount");
		const currentValue = this.selectedAccountSubject.getValue();
		if ((currentValue && currentValue.ID === value.ID) && !forceTrigger) {
			this.SetSelectedAccountGUID(value.GUID || "");
			return; // ❌ Prevent unnecessary emission
		}

		this.SetSelectedAsset({} as ILevelAsset, forceTrigger); // Clear the selected asset when changing organisation

		this.selectedAccountSubject.next(value);
		if (value) { localStorage.setItem("selectedAccount", JSON.stringify(value)); }
		else { localStorage.removeItem("selectedAccount"); }
		this.SetSelectedAccountGUID(value.GUID || "");
	}


	/**
	 * Set the Selected Account GUID
	 * @param value
	 * @param forceTrigger
	 */
	SetSelectedAccountGUID(value: string, forceTrigger: boolean = false) {
		const currentValue = this.selectedAccountGUIDSubject.getValue();
		if ((currentValue && currentValue === value) && !forceTrigger) {
			return; // ❌ Prevent unnecessary emission
		}
		this.selectedAccountGUIDSubject.next(value);
		if (value) { localStorage.setItem("selectedAccountGUID", value); }
		else { localStorage.removeItem("selectedAccountGUID"); }
	}
	/**
	 * Set the Selected Organisation
	 * @param value
	 * @param forceTrigger
	 */
	SetSelectedOrganisation(value: IOrganisation = {} as IOrganisation, forceTrigger: boolean = false) {
		localStorage.removeItem("selectedOrganisation");
		const currentValue = this.selectedOrganisationSubject.getValue();
		if ((currentValue && currentValue.Id === value.Id) && !forceTrigger) {
			return; // ❌ Prevent unnecessary emission
		}

		this.SetSelectedAsset({} as ILevelAsset, forceTrigger); // Clear the selected asset when changing organisation

		this.selectedOrganisationSubject.next(value);
		if (value) { localStorage.setItem("selectedOrganisation", JSON.stringify(value)); }
		else { localStorage.removeItem("selectedOrganisation"); }

		this.SetSelectedOrganisationUID(value.Uid || "");

	}

	/**
	 * Set the Selected Organisation ID
	 * @param value
	 * @param forceTrigger
	 */
	SetSelectedOrganisationUID(value: string, forceTrigger: boolean = false) {
		const currentValue = this.selectedOrganisationUIDSubject.getValue();
		if ((currentValue && currentValue === value) && !forceTrigger) {
			return; // ❌ Prevent unnecessary emission
		}

		this.selectedOrganisationUIDSubject.next(value);
		if (value) { localStorage.setItem("selectedOrganisationUID", value); }
		else { localStorage.removeItem("selectedOrganisationUID"); }
	}

	/**
	 * Set the Selected SubFolder
	 * @param value
	 */
	SetSelectedSubLocation(value: string) {
		this.selectedSubLocationSubject.next(value);
		if (value) { localStorage.setItem("selectedSubLocation", JSON.stringify(value)); }
		else { localStorage.removeItem("selectedSubLocation"); }
		this.SetSelectedAsset({} as ILevelAsset, true); // Clear the selected asset when changing organisation
	}

	/**
	 * Set the Selected Location in the Hierarchy
	 * @param value 
	 */
	SetSelectedhierarchicalLocation(value: IOrganisationsHierarchy) {
		this.selectedHierarchicalLocationSubject.next(value);
		if (value) { localStorage.setItem("hierarchicalLocationData", JSON.stringify(value)); }
		else { localStorage.removeItem("hierarchicalLocationData"); }
		this.SetSelectedAsset({} as ILevelAsset, true); // Clear the selected asset when changing organisation
	}

	/**
	 * Update Accounts List
	 * @param list
	 */
	setMyAccounts(accounts: IAccount[] = []) {
		accounts = this.normalizeFeatures(accounts) as Account[];
		this.myAccountsSubject.next(accounts);
		localStorage.setItem("myAccounts", JSON.stringify(accounts));
	}

	/**
	 * Set Account Users List
	 * @param list
	 */
	setAccountUsers(value: IAccountUser[]) {
		try {
			this.accountUsersSubject.next(value);
		} catch { }
	}

	/**
	 * Update Accounts List
	 * @param data
	 */
	async RefreshMyAccounts(data: any, assignGlobal: boolean = true): Promise<IAccount[]> {
		try {

			// Check the Account Service exists in the Injector (to avoid circular dependency), if not get it
			if (this.accountsService == null) { this.getAccountsService(); }

			// Call the Account Service to get the Accounts
			const response = await firstValueFrom(
				this.accountsService.getAccounts$(data)
			);

			// Update the Accounts List
			if (assignGlobal) { this.setMyAccounts(response); }

			// Return the response
			return response;

		} catch (error: any) {
			// If there is an error, clear the Accounts List if assignGlobal is true
			if (assignGlobal) { this.setMyAccounts(); }

			// Handle the error
			if (error?.status === 404) {
				this.globalFunctions.HandleError(error, "SiteSubscriptions.RefreshMyAccounts > getAccounts: Error retrieving Accounts");
			} else {
				this.globalFunctions.HandleError(error, "SiteSubscriptions.RefreshMyAccounts > getAccounts: Error retrieving Accounts");
			}

			// Return an empty array in case of error
			return [];

		}
	}


	/**
	 * normalizeFeatures
	 * @description Normalizes the Features property of an Account or array of Accounts to ensure it is always an object, parsing from JSON if necessary.
	 * @param input An Account or array of Accounts to normalize.
	 * @returns The input with normalized Features properties.
	 * @remarks This method checks if the Features property is a string and attempts to parse it as JSON. If parsing fails or if the property is missing, it defaults to an empty object. If the input is an array, it normalizes each account in the array.
	 */
	normalizeFeatures(input: Account | Account[]): Account | Account[] {

		const normalize = (account: Account): Account => {
			const features = account.Features;

			if (!features) {
				account.Features = {};
				return account;
			}

			if (Array.isArray(features)) {
				return account; // already good
			}

			if (typeof features === 'object') {
				return account; // already parsed
			}

			if (typeof features === 'string') {
				try {
					account.Features = JSON.parse(features);
				} catch {
					account.Features = {};
				}
			} else {
				account.Features = {};
			}

			return account;
		};

		if (Array.isArray(input)) {
			return input.map(normalize);
		}

		return normalize(input);
	}

	/**
	 * RefreshUserList
	 * @param refresh
	 */
	RefreshUserList(refresh: boolean) {
		this.refreshUserListSubject.next(refresh)
	}

	/**
	 * RefreshWatchList
	 * @param refresh refresh 
	 */
	RefreshWatchList(refresh: boolean) {
		this.refreshWatchListSubject.next(refresh)
	}

	/**
	 * RefreshChart
	 * @param refresh refresh 
	 */
	RefreshChart(refresh: boolean) {
		this.refreshChartSubject.next(refresh)
	}

	/**
	 * ClearAndRefreshChart
	 * @param refresh refresh  
	 */
	ClearAndRefreshChart(refresh: boolean) {
		this.clearAndRefreshChartSubject.next(refresh)
	}

	/**
	 * UpdateModelList
	 * @param refresh refresh  
	 */
	UpdateModelList(refresh: boolean) {
		this.updateModelListSubject.next(refresh)
	}

	/**
	 * RefreshModelEdits
	 * @param refresh refresh  
	 */
	RefreshModelEdits(refresh: boolean) {
		this.refreshModelEditsSubject.next(refresh)
	}

	/**
	 * RefreshTileValues
	 * @param refresh refresh  
	 */
	RefreshTileValues(refresh: boolean) {
		this.refreshTileValuesSubject.next(refresh)
	}

	/**
	 * RedrawDashboard
	 * @param redraw redraw 
	 */
	RedrawDashboard(redraw: boolean) {
		this.redrawDashboardSubject.next(redraw)
	}

	/**
	 * RefreshMyOrganisations
	 * @param accountGUID 
	 * @param OrganisationID 
	 * @param assignGlobal 
	 * @returns 
	 */
	async RefreshMyOrganisations(accountGUID: string = "", OrganisationID: string = "", assignGlobal = true): Promise<IOrganisation[]> {
		try {

			// Check the Account Service exists in the Injector (to avoid circular dependency), if not get it
			if (this.organisationDataService == null) { this.getOrganisationDataService(); }

			// Call the Account Service to get the Organisations
			const response = await firstValueFrom(
				this.organisationDataService.getOrganisations$(accountGUID)
			);
			// Update the Organisations List
			if (assignGlobal) {
				this.setmyOrganisations(response);
			}

			// Return the response
			return response;

		}
		catch (error: any) {
			// If there is an error, clear the Organisations List if assignGlobal is true
			if (assignGlobal) {
				this.setmyOrganisations();
			}

			// Handle the error
			if (error?.status === 404) {
				this.globalFunctions.HandleError(error, "getOrganisations: Error retrieving organisations");
			} else {
				this.globalFunctions.HandleError(error, "");
			}

			// Return an empty array in case of error
			return [];

		}

	}

	/**
	 * Update Accounts List
	 * @param organisations
	 */
	setmyOrganisations(value: IOrganisation[] = []) {
		try {
			const currentValue = this.myOrganisationsSubject.getValue();
			if (currentValue && JSON.stringify(currentValue) === JSON.stringify(value)) {
				return; // ❌ Prevent unnecessary emission
			}

			this.myOrganisationsSubject.next(value);
			if (value.length > 0) { localStorage.setItem("myOrganisations", JSON.stringify(value)); }
			else { localStorage.removeItem("myOrganisations"); }
		} catch { }
	}

}